import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../../components/ui/dialog';
import { Plus, Trash2, Loader2, Edit2 } from 'lucide-react';
import Cookies from 'js-cookie';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Topics() {
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTopic, setNewTopic] = useState({ name: '', description: '' });
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({ name: '', description: '' });
  const [editOpen, setEditOpen] = useState(false);

  useEffect(() => {
    fetchTopics();
  }, []);

  const fetchTopics = async () => {
    try {
      const token = Cookies.get('admin_token');
      const response = await axios.get(`${API}/master/topics`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setTopics(response.data.data || []);
    } catch (err) {
      toast.error('Failed to load topics');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newTopic.name.trim()) return;

    setAdding(true);
    try {
      const token = Cookies.get('admin_token');
      const submitData = {
        name: newTopic.name
      };
      if (newTopic.description && newTopic.description.trim()) {
        submitData.description = newTopic.description;
      }
      await axios.post(`${API}/master/topics`, 
        submitData,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      toast.success('Topic added successfully');
      setNewTopic({ name: '', description: '' });
      setOpen(false);
      fetchTopics();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add topic');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this topic?')) return;

    try {
      const token = Cookies.get('admin_token');
      await axios.delete(`${API}/master/topics/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Topic deleted');
      fetchTopics();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete topic');
    }
  };

  const handleEdit = (topic) => {
    setEditingId(topic.id);
    setEditData({ name: topic.name, description: topic.description || '' });
    setEditOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editData.name.trim()) return;

    try {
      const token = Cookies.get('admin_token');
      const submitData = {
        name: editData.name
      };
      if (editData.description && editData.description.trim()) {
        submitData.description = editData.description;
      }
      await axios.put(`${API}/master/topics/${editingId}`, submitData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Topic updated successfully');
      setEditOpen(false);
      setEditingId(null);
      fetchTopics();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update topic');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-heading mb-2">Topics</h1>
          <p className="text-muted-foreground">Manage topics for stories and articles</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button variant="editorial">
              <Plus className="h-4 w-4 mr-2" /> Add Topic
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Topic</DialogTitle>
              <DialogDescription>Create a new topic for content categorization</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAdd}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="topic">Topic Name *</Label>
                  <Input
                    id="topic"
                    value={newTopic.name}
                    onChange={(e) => setNewTopic({...newTopic, name: e.target.value})}
                    placeholder="e.g., AI & Machine Learning, Fintech"
                    disabled={adding}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description (Optional)</Label>
                  <Textarea
                    id="description"
                    value={newTopic.description}
                    onChange={(e) => setNewTopic({...newTopic, description: e.target.value})}
                    placeholder="Brief description of this topic"
                    disabled={adding}
                    rows={3}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={adding}>
                  Cancel
                </Button>
                <Button type="submit" disabled={adding || !newTopic.name.trim()}>
                  {adding ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Adding...</>
                  ) : (
                    'Add Topic'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Edit Topic Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Topic</DialogTitle>
            <DialogDescription>Update topic information</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdate}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-topic">Topic Name *</Label>
                <Input
                  id="edit-topic"
                  value={editData.name}
                  onChange={(e) => setEditData({...editData, name: e.target.value})}
                  placeholder="e.g., AI & Machine Learning, Fintech"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-description">Description (Optional)</Label>
                <Textarea
                  id="edit-description"
                  value={editData.description}
                  onChange={(e) => setEditData({...editData, description: e.target.value})}
                  placeholder="Brief description of this topic"
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={!editData.name.trim()}>
                Update Topic
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Topics List */}
      <Card>
        <CardHeader>
          <CardTitle>All Topics ({topics.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
            </div>
          ) : topics.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">No topics added yet</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {topics.map((topic) => (
                <div
                  key={topic.id}
                  className="flex flex-col justify-between p-4 border border-border rounded-lg hover:bg-accent/50 transition-colors"
                >
                  <div>
                    <p className="font-medium mb-1">{topic.name}</p>
                    {topic.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2">{topic.description}</p>
                    )}
                  </div>
                  <div className="flex gap-1 mt-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(topic)}
                    >
                      <Edit2 className="h-4 w-4 text-primary" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(topic.id)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
